import { ReactNode, useState } from "react";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Collapse, IconButton } from "@mui/material";
import DSBox from "./DSBox";
import DSTypography from "./DSTypography";

type DSHintProps = {
  /** The field's title; the "?" sits right after it. */
  label: ReactNode;
  /** The explanation shown when the "?" is opened. */
  children: ReactNode;
  /** Open on first render (e.g. for a required step). Defaults to closed. */
  defaultOpen?: boolean;
};

// A title with a "?" that expands the explanation below it. Use it for every
// hint instead of permanent helper text, so forms stay short.
const DSHint = ({ label, children, defaultOpen = false }: DSHintProps) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <DSBox sx={{ mb: 0.5 }}>
      <DSBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <DSTypography variant="subtitle2">{label}</DSTypography>
        <IconButton
          size="small"
          aria-label={open ? "Hide hint" : "Show hint"}
          aria-expanded={open}
          color={open ? "primary" : "default"}
          onClick={() => setOpen((value) => !value)}
        >
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
      </DSBox>
      <Collapse in={open} unmountOnExit>
        <DSBox
          sx={{
            mt: 0.5,
            mb: 1,
            p: 1.5,
            borderRadius: 1,
            backgroundColor: "action.hover",
          }}
        >
          <DSBox sx={{ typography: "body2" }}>{children}</DSBox>
        </DSBox>
      </Collapse>
    </DSBox>
  );
};

export default DSHint;
